import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Editor } from "@tinymce/tinymce-react";
import {
	SingleImageDropzone,
	MultipleImageDropzone,
} from "../DropzoneComponents";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import MultiSelectTags from "../../components/shared/MultiSelectTags";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FileDropzone from "../FileDropzone";
// ───────── helper ─────────
function ensureParents(ids, all) {
	const out = new Set(ids);
	const parentOf = Object.fromEntries(all.map((c) => [c.id, c.parent]));
	ids.forEach((id) => {
		let p = parentOf[id];
		while (p) {
			out.add(p);
			p = parentOf[p];
		}
	});
	return [...out];
}

function renderField(el, fieldValues, setFieldValues) {
	const handleChange = (e) => {
		setFieldValues((prev) => ({ ...prev, [el.name]: e.target.value }));
	};

	switch (el.type) {
		case "text":
		case "shortDate":
		case "longDate":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<input
						type={
							el.type === "shortDate"
								? "date"
								: el.type === "longDate"
									? "datetime-local"
									: "text"
						}
						className="form-control"
						placeholder={el.placeholder || ""}
						value={fieldValues[el.name] || ""}
						onChange={handleChange}
						required={!!el.required}
					/>
				</div>
			);

		case "textarea":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<textarea
						className="form-control"
						rows={3}
						placeholder={el.placeholder || ""}
						value={fieldValues[el.name] || ""}
						onChange={handleChange}
						required={!!el.required}
					/>
				</div>
			);

		case "editor":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<Editor
						tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
						value={fieldValues[el.name] || ""}
						onEditorChange={(content) =>
							setFieldValues((prev) => ({ ...prev, [el.name]: content }))
						}
						init={{
							promotion: false,
							branding: false,
							height: 400,
							plugins: [
								"advlist",
								"autolink",
								"link",
								"image",
								"lists",
								"charmap",
								"preview",
								"anchor",
								"pagebreak",
								"searchreplace",
								"wordcount",
								"visualblocks",
								"code",
								"fullscreen",
								"insertdatetime",
								"media",
								"table",
								"emoticons",
								"help",
							],
							toolbar:
								"undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
								"bullist numlist outdent indent | link fileupload image | " +
								"print preview media fullscreen code | forecolor backcolor emoticons | help",
							content_style:
								"body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
							image_advtab: true,
							setup: (editor) => {
								editor.ui.registry.addButton("fileupload", {
									text: "Dosya Ekle",
									tooltip: "Seçili yazıya dosya bağlantısı ekle",

									onAction: () => {
										/*
										 * Dosya penceresi açılmadan önce editördeki
										 * seçim konumunu saklıyoruz.
										 */
										const bookmark = editor.selection.getBookmark(2, true);
										const hasSelectedText = !editor.selection.isCollapsed();

										const input = document.createElement("input");

										input.type = "file";
										input.accept =
											".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv";

										input.onchange = async () => {
											const file = input.files?.[0];

											if (!file) {
												return;
											}

											try {
												const token = localStorage.getItem("admin_token");

												const formData = new FormData();
												formData.append("file", file);

												const response = await fetch(
													`${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
													{
														method: "POST",
														headers: {
															Authorization: `Bearer ${token}`,
														},
														body: formData,
													},
												);

												if (!response.ok) {
													throw new Error(
														`Dosya yüklenemedi: ${response.status}`,
													);
												}

												const data = await response.json();

												if (!data.location) {
													throw new Error(
														"Sunucudan dosya bağlantısı dönmedi.",
													);
												}

												/*
												 * Editöre yeniden odaklanıp daha önce
												 * sakladığımız seçimi geri getiriyoruz.
												 */
												editor.focus();
												editor.selection.moveToBookmark(bookmark);

												if (hasSelectedText) {
													/*
													 * Seçili yazıyı silmeden linke dönüştürür.
													 */
													editor.execCommand("mceInsertLink", false, {
														href: data.location,
														target: "_blank",
														rel: "noopener noreferrer",
														download: file.name,
													});
												} else {
													/*
													 * Yazı seçilmemişse dosya adını bağlantı olarak ekler.
													 */
													editor.insertContent(
														`<a href="${data.location}"
								target="_blank"
								rel="noopener noreferrer"
								download="${editor.dom.encode(file.name)}"
							>${editor.dom.encode(file.name)}</a>`,
													);
												}

												toast.success("Dosya bağlantısı eklendi.");
											} catch (error) {
												console.error("TinyMCE dosya yükleme hatası:", error);

												toast.error(error.message || "Dosya yüklenemedi.");
											}
										};

										input.click();
									},
								});
							},

							// ✅ Bu 3 satır base64'ü tamamen engeller
							automatic_uploads: true,
							file_picker_types: "image file",
							block_unsupported_drop: false,

							// ✅ paste'den gelen base64'leri de yakalar
							paste_data_images: false, // kopyala-yapıştır ile gelen resimleri engelle

							images_upload_handler: (blobInfo) => {
								return new Promise((resolve, reject) => {
									const token = localStorage.getItem("admin_token");
									const formData = new FormData();
									formData.append("file", blobInfo.blob(), blobInfo.filename());

									fetch(
										`${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
										{
											method: "POST",
											headers: {
												Authorization: `Bearer ${token}`,
											},
											body: formData,
										},
									)
										.then((res) => {
											if (!res.ok)
												throw new Error("Upload failed: " + res.status);
											return res.json();
										})
										.then((data) => {
											console.log("✅ TinyMCE upload response:", data);
											if (!data.location) throw new Error("location alanı yok");
											resolve(data.location); // ✅ sadece URL string dön
										})
										.catch((err) => {
											console.error("❌ TinyMCE upload error:", err);
											reject(err.message);
										});
								});
							},
						}}
					/>
				</div>
			);

		case "image":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">
						{el.label} ({el.maxWidth}x{el.maxHeight})
					</label>
					<SingleImageDropzone
						fieldName={el.name}
						fieldValue={fieldValues[el.name]}
						setFieldValues={setFieldValues}
						required={!!el.required}
						maxWidth={el.maxWidth} // from your JSON
						maxHeight={el.maxHeight} // from your JSON
					/>
				</div>
			);

		case "images":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">
						{el.label} ({el.maxWidth}x{el.maxHeight})
					</label>
					<MultipleImageDropzone
						fieldName={el.name}
						fieldValue={fieldValues[el.name]}
						setFieldValues={setFieldValues}
						required={!!el.required}
						maxWidth={el.maxWidth} // <-- Bunu ekliyoruz
						maxHeight={el.maxHeight}
					/>
				</div>
			);

		case "file":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>

					<FileDropzone
						value={fieldValues[el.name] || ""}
						onChange={(file) =>
							setFieldValues((prev) => ({
								...prev,
								[el.name]: file,
							}))
						}
						required={!!el.required}
						accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv"
					/>
				</div>
			);

		case "files":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>

					<FileDropzone
						multiple
						value={
							Array.isArray(fieldValues[el.name]) ? fieldValues[el.name] : []
						}
						onChange={(files) =>
							setFieldValues((prev) => ({
								...prev,
								[el.name]: files,
							}))
						}
						required={!!el.required}
						accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv"
					/>
				</div>
			);

		case "select": {
			const titles = (el.content_titles || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const values = (el.content_values || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const opts = values.map((v, i) => ({
				value: v,
				label: titles[i] || v, // title yoksa value göster
			}));

			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<Select
						options={opts}
						value={opts.find((o) => o.value === fieldValues[el.name]) || null}
						isClearable={!el.required}
						placeholder={el.placeholder || "Seçin"}
						onChange={(o) =>
							setFieldValues((prev) => ({
								...prev,
								[el.name]: o ? o.value : "",
							}))
						}
					/>
				</div>
			);
		}

		case "multiselect": {
			const titles = (el.content_titles || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const values = (el.content_values || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const opts = values.map((v, i) => ({
				value: v,
				label: titles[i] || v,
			}));

			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<CreatableSelect
						isMulti
						options={opts}
						value={
							Array.isArray(fieldValues[el.name])
								? opts.filter((o) => fieldValues[el.name].includes(o.value))
								: []
						}
						placeholder={el.placeholder || "Seçim yapın"}
						onChange={(arr) =>
							setFieldValues((prev) => ({
								...prev,
								[el.name]: arr.map((o) => o.value),
							}))
						}
					/>
				</div>
			);
		}

		default:
			return null;
	}
}

const ModuleDataCreate = () => {
	const { slug } = useParams();
	const navigate = useNavigate();

	const [moduleInfo, setModuleInfo] = useState(null);
	const [fieldValues, setFieldValues] = useState({});
	const [loading, setLoading] = useState(false);
	const [seoData, setSeoData] = useState({
		seo_title: "",
		seo_description: "",
	});

	// kategori
	const [allCats, setAllCats] = useState([]);
	const [options, setOptions] = useState([]);
	const [selected, setSelected] = useState([]);
	const [selectedCats, setSelectedCats] = useState([]);

	useEffect(() => {
		const fetchModuleInfo = async () => {
			try {
				const token = localStorage.getItem("admin_token");
				const resp = await axios.get(
					`${process.env.REACT_APP_API_BASE_URL}/api/modules/by-slug/${slug}`,
					{ headers: { Authorization: `Bearer ${token}` } },
				);
				console.log("🍀 moduleInfo:", resp.data);
				setModuleInfo(resp.data);
			} catch (err) {
				console.error("Modül bilgisi alınamadı:", err);
			}
		};
		fetchModuleInfo();
	}, [slug]);

	// ─ fetch categories if needed
	useEffect(() => {
		if (!moduleInfo || Number(moduleInfo.category) !== 1) return;
		(async () => {
			const token = localStorage.getItem("admin_token");
			const { data } = await axios.get(
				`${process.env.REACT_APP_API_BASE_URL}/api/category?module_id=${moduleInfo.id}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			setAllCats(data);

			// hiyerarşik etiket ‒ ‘— ’ ile seviye göster
			const tree = (items, pid = 0, lvl = 0) =>
				items
					.filter((i) => i.parent === pid)
					.flatMap((i) => [
						{ value: i.id, label: `${"— ".repeat(lvl)}${i.name}` },
						...tree(items, i.id, lvl + 1),
					]);
			setOptions(tree(data));
		})().catch(console.error);
	}, [moduleInfo]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		console.log("🚀 SUBMIT TETIKLENDI");

		if (!moduleInfo) return;
		setLoading(true);

		try {
			const token = localStorage.getItem("admin_token");
			const lang =
				new URLSearchParams(window.location.search).get("lang") || "tr";

			const formData = new FormData();
			formData.append("module_id", moduleInfo.id);
			formData.append("slug", slug);

			formData.append("lang", lang);
			formData.append("org_id", "0");
			formData.append("seo_title", seoData.seo_title.trim());
			formData.append("seo_description", seoData.seo_description.trim());

			if (Number(moduleInfo.category) === 1) {
				formData.append(
					"category",
					selectedCats.length ? selectedCats.join(",") : "", // hiç seçilmediyse boş string
				);
			}

			const dataCopy = { ...fieldValues };

			Object.entries(dataCopy).forEach(([key, value]) => {
				// Tek dosya
				if (value instanceof File) {
					formData.append(key, value);
					delete dataCopy[key];
					return;
				}

				// Çoklu dosya
				if (Array.isArray(value)) {
					const existingFiles = value.filter(
						(item) => typeof item === "string" && item.trim() !== "",
					);

					const newFiles = value.filter((item) => item instanceof File);

					newFiles.forEach((file) => {
						formData.append(`${key}[]`, file);
					});

					if (existingFiles.length > 0) {
						dataCopy[key] = existingFiles;
					} else {
						delete dataCopy[key];
					}
				}
			});

			formData.append("data", JSON.stringify(dataCopy));

			// Debug log
			console.log("📤 Gönderilecek data:", dataCopy);
			for (let pair of formData.entries()) {
				console.log("🧾 FormData:", pair[0], pair[1]);
			}

			await axios.post(
				`${process.env.REACT_APP_API_BASE_URL}/api/module-data/${slug}`,
				formData,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);
			// Başarılıysa toast ve yönlendirme
			toast.success("Kayıt başarıyla oluşturuldu!");
			// 1 sn sonra tabloya yönlendir
			setTimeout(() => {
				navigate(`/modules/${slug}`);
				// burayı kendi listeleme rotana göre güncelle
			}, 1000);
		} catch (err) {
			console.error("Kayıt eklenemedi:", err);
			alert("Kayıt oluşturulurken hata oluştu!");
		} finally {
			setLoading(false);
		}
	};

	if (!moduleInfo) return <p>Yükleniyor...</p>;

	let dynamicElements = [];
	try {
		if (moduleInfo.elements) {
			dynamicElements = JSON.parse(moduleInfo.elements).map((el) => ({
				...el,
				// maxWidth/maxHeight artık string => number
				...(el.maxWidth != null && { maxWidth: Number(el.maxWidth) }),
				...(el.maxHeight != null && { maxHeight: Number(el.maxHeight) }),
			}));
		}
	} catch (e) {
		console.error("Elements parse hatası:", e);
	}

	return (
		<div className="container my-4">
			<ToastContainer />

			<form onSubmit={handleSubmit}>
				{/* ================= İÇERİK KARTI ================= */}
				<div className="card shadow-sm mb-4">
					<div className="card-header bg-white py-3">
						<h5 className="mb-0 fw-bold">
							{moduleInfo.name || "İçerik Bilgileri"}
						</h5>
					</div>

					<div className="card-body p-4">
						{/* ===== KATEGORİ SEÇİMİ ===== */}
						{Number(moduleInfo.category) === 1 && (
							<div className="mb-4">
								<label className="form-label fw-semibold">Kategoriler</label>

								{Number(moduleInfo.multiple_category) === 1 ? (
									<Select
										isMulti
										options={options}
										closeMenuOnSelect={false}
										placeholder="Kategori seçin"
										value={options.filter((option) =>
											selectedCats.includes(option.value),
										)}
										onChange={(selectedOptions) => {
											const ids = selectedOptions.map((option) => option.value);

											setSelectedCats(ensureParents(ids, allCats));
										}}
									/>
								) : (
									<Select
										options={options}
										isClearable
										placeholder="Kategori seçin"
										value={
											options.find(
												(option) => option.value === selectedCats[0],
											) || null
										}
										onChange={(option) => {
											const ids = option
												? ensureParents([option.value], allCats)
												: [];

											setSelectedCats(ids);
										}}
									/>
								)}
							</div>
						)}

						{dynamicElements.map((element) =>
							renderField(element, fieldValues, setFieldValues),
						)}
					</div>
				</div>

				{/* ================= SEO KARTI ================= */}
				<div className="card shadow-sm mb-4">
					<div className="card-header bg-white py-3">
						<h5 className="mb-0 fw-bold">SEO Ayarları</h5>
					</div>

					<div className="card-body p-4">
						<div className="mb-3">
							<label htmlFor="seo_title" className="form-label fw-bold">
								SEO Başlık
							</label>

							<input
								type="text"
								id="seo_title"
								name="seo_title"
								className="form-control"
								value={seoData.seo_title}
								maxLength={255}
								placeholder="Arama motorlarında görünecek başlık"
								onChange={(event) =>
									setSeoData((previous) => ({
										...previous,
										seo_title: event.target.value,
									}))
								}
							/>

							<div className="form-text text-end">
								{seoData.seo_title.length}/255
							</div>
						</div>

						<div className="mb-0">
							<label htmlFor="seo_description" className="form-label fw-bold">
								SEO Açıklama
							</label>

							<textarea
								id="seo_description"
								name="seo_description"
								className="form-control"
								rows={4}
								maxLength={500}
								value={seoData.seo_description}
								placeholder="Arama motorlarında görünecek sayfa açıklaması"
								onChange={(event) =>
									setSeoData((previous) => ({
										...previous,
										seo_description: event.target.value,
									}))
								}
							/>

							<div className="form-text text-end">
								{seoData.seo_description.length}/500
							</div>
						</div>
					</div>
				</div>

				{/* ================= KAYDET BUTONU ================= */}
				<div className="d-flex justify-content-center">
					<button
						type="submit"
						className="btn btn-primary px-5"
						disabled={loading}
					>
						{loading ? "Kaydediliyor..." : "Kaydet"}
					</button>
				</div>
			</form>
		</div>
	);
};

export default ModuleDataCreate;
